import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useClub } from '@/contexts/ClubContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { User, Target, Save, Package, Edit2, Search } from 'lucide-react';
import StarRating from '@/components/StarRating';
import { CardGridSkeleton, PageHeaderSkeleton, SectionCardSkeleton } from '@/components/PageSkeletons';
import apiClient from '@/services/apiClient';
import { billingService } from '@/services/storageService';
import { mapEvaluationsFromApi } from '@/lib/clubApiMappers';
import { useI18n } from '@/contexts/I18nContext';
import { createAvatar } from '@dicebear/core';
import { 
  adventurer,
  avataaars,
  bottts,
  lorelei,
  micah,
  toonHead,
  openPeeps
} from '@dicebear/collection';

// 30 consistent, named avatars - MIXED order with badminton-themed names
type DiceBearStyle = Parameters<typeof createAvatar>[0];
type AvatarSelection = {
  avatar_id: string;
  avatar_seed: string;
  collection_name: string;
  avatar_name: string;
} | undefined;

const CONSISTENT_AVATARS: Array<{
  id: string;
  style: string;
  styleName: string;
  seed: string;
  collection: DiceBearStyle | undefined;
  name: string;
}> = [
  // Adventurer (5) - Badminton themed
  { id: 'adventurer-1', style: 'adventurer', styleName: 'Adventurer', seed: 'smash-king', collection: adventurer, name: 'Smash Master' },
  { id: 'adventurer-2', style: 'adventurer', styleName: 'Adventurer', seed: 'net-wizard', collection: adventurer, name: 'Net Wizard' },
  { id: 'adventurer-3', style: 'adventurer', styleName: 'Adventurer', seed: 'drop-shot', collection: adventurer, name: 'Drop Shot' },
  { id: 'adventurer-4', style: 'adventurer', styleName: 'Adventurer', seed: 'clear-star', collection: adventurer, name: 'Clear Star' },
  { id: 'adventurer-5', style: 'adventurer', styleName: 'Adventurer', seed: 'rally-ace', collection: adventurer, name: 'Rally Ace' },
  
  // Avataaars (5) - Badminton themed
  { id: 'avataaars-1', style: 'avataaars', styleName: 'Avataaars', seed: 'shuttle-pro', collection: avataaars, name: 'Shuttle Pro' },
  { id: 'avataaars-2', style: 'avataaars', styleName: 'Avataaars', seed: 'court-hero', collection: avataaars, name: 'Court Hero' },
  { id: 'avataaars-3', style: 'avataaars', styleName: 'Avataaars', seed: 'backhand-king', collection: avataaars, name: 'Backhand King' },
  
  // Bottts (5) - Badminton themed
  { id: 'bottts-1', style: 'bottts', styleName: 'Bottts', seed: 'drill-bot', collection: bottts, name: 'Drill Bot' },
  { id: 'bottts-2', style: 'bottts', styleName: 'Bottts', seed: 'footwork-pro', collection: bottts, name: 'Footwork Pro' },
  
  // Lorelei (5) - Badminton themed
  { id: 'lorelei-1', style: 'lorelei', styleName: 'Lorelei', seed: 'feather-flight', collection: lorelei, name: 'Feather Flight' },
  { id: 'lorelei-2', style: 'lorelei', styleName: 'Lorelei', seed: 'swift-racquet', collection: lorelei, name: 'Swift Racquet' },
  { id: 'lorelei-3', style: 'lorelei', styleName: 'Lorelei', seed: 'birdie-chaser', collection: lorelei, name: 'Birdie Chaser' },
  
  // Micah (5) - Badminton themed
  { id: 'micah-1', style: 'micah', styleName: 'Micah', seed: 'smash-queen', collection: micah, name: 'Smash Queen' },
  { id: 'micah-2', style: 'micah', styleName: 'Micah', seed: 'net-player', collection: micah, name: 'Net Player' },
  { id: 'micah-3', style: 'micah', styleName: 'Micah', seed: 'drive-master', collection: micah, name: 'Drive Master' },
  
  // Toon Heads (5) - Fixed from thumbs to toonHead
  { id: 'toonhead-1', style: 'toonHead', styleName: 'Toon Heads', seed: 'happy-shuttle', collection: toonHead, name: 'Happy Shuttle' },
  { id: 'toonhead-2', style: 'toonHead', styleName: 'Toon Heads', seed: 'jumping-smash', collection: toonHead, name: 'Jumping Smash' },
  { id: 'toonhead-3', style: 'toonHead', styleName: 'Toon Heads', seed: 'court-star1', collection: toonHead, name: 'Court Star' },
  
  // Open Peeps (5) - Badminton themed
  { id: 'openpeeps-1', style: 'openPeeps', styleName: 'Open Peeps', seed: 'tournament-pro', collection: openPeeps, name: 'Tournament Pro' },
  { id: 'openpeeps-2', style: 'openPeeps', styleName: 'Open Peeps', seed: 'doubles-champ', collection: openPeeps, name: 'Doubles Champ' },
  { id: 'openpeeps-3', style: 'openPeeps', styleName: 'Open Peeps', seed: 'singles-ac', collection: openPeeps, name: 'Singles Ace' },
  
  // Continue mixing remaining avatars
  { id: 'avataaars-4', style: 'avataaars', styleName: 'Avataaars', seed: 'forehand-pro', collection: avataaars, name: 'Forehand Pro' },
  { id: 'avataaars-5', style: 'avataaars', styleName: 'Avataaars', seed: 'lifter-legend', collection: avataaars, name: 'Lifter Legend' },
  
  { id: 'bottts-3', style: 'bottts', styleName: 'Bottts', seed: 'service-bot', collection: bottts, name: 'Service Bot' },
  { id: 'bottts-4', style: 'bottts', styleName: 'Bottts', seed: 'smash-bot', collection: bottts, name: 'Smash Bot' },
  { id: 'bottts-5', style: 'bottts', styleName: 'Bottts', seed: 'rally-bot', collection: bottts, name: 'Rally Bot' },
  
  { id: 'lorelei-4', style: 'lorelei', styleName: 'Lorelei', seed: 'quick-reflex1', collection: lorelei, name: 'Quick Reflex' },
  { id: 'lorelei-5', style: 'lorelei', styleName: 'Lorelei', seed: 'agile-player', collection: lorelei, name: 'Agile Player' },
  
  { id: 'micah-4', style: 'micah', styleName: 'Micah', seed: 'slam-dunk', collection: micah, name: 'Slam Dunk' },
  { id: 'micah-5', style: 'micah', styleName: 'Micah', seed: 'spin-master', collection: micah, name: 'Spin Master' },
  
  { id: 'toonhead-4', style: 'toonHead', styleName: 'Toon Heads', seed: 'bouncy-birdie', collection: toonHead, name: 'Bouncy Birdie' },
  { id: 'toonhead-5', style: 'toonHead', styleName: 'Toon Heads', seed: 'cheeky-shuttle1', collection: toonHead, name: 'Cheeky Shuttle' },
  
  { id: 'openpeeps-4', style: 'openPeeps', styleName: 'Open Peeps', seed: 'coach-favorite5', collection: openPeeps, name: 'Coach Favorite' },
  { id: 'openpeeps-5', style: 'openPeeps', styleName: 'Open Peeps', seed: 'team-captain', collection: openPeeps, name: 'Team Captain' },
];

const MIXED_AVATARS = [...CONSISTENT_AVATARS];

// Default avatar options
const DEFAULT_AVATAR_OPTIONS = {
  radius: 50,
  backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf'],
  randomizeIds: true,
};

const normalizeCollectionName = (collection?: string | null) => {
  if (!collection) return undefined;
  const key = collection.trim();
  const map: Record<string, string> = {
    'toonhead': 'toon-head',
    'toon-head': 'toon-head',
    'toonHead': 'toon-head',
    'openpeeps': 'open-peeps',
    'open-peeps': 'open-peeps',
    'openPeeps': 'open-peeps',
  };
  const lower = key.toLowerCase();
  return map[lower] || key;
};

export default function Profile() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { activeClubId, currentClubRole } = useClub();
  const queryClient = useQueryClient();
  const clubId = activeClubId ? Number(activeClubId) : null;

  // Avatar state
  const [selectedAvatarId, setSelectedAvatarId] = useState<string>(MIXED_AVATARS[0].id);
  const [selectedAvatarUrl, setSelectedAvatarUrl] = useState<string>('');
  const [isAvatarDialogOpen, setIsAvatarDialogOpen] = useState(false);
  const [avatarUrls, setAvatarUrls] = useState<Record<string, string>>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSavingAvatar, setIsSavingAvatar] = useState(false);

  // Profile data
  const profileQuery = useQuery({
    queryKey: ['profile'],
    queryFn: () => apiClient.request('/profile/'),
    enabled: Boolean(user),
  });

  const profile = profileQuery.data as any;
  const billingQuery = useQuery({
    queryKey: ['billing-status', clubId],
    queryFn: () => billingService.getStatus(clubId),
    enabled: Boolean(user && clubId),
  });
  const resolvedPackageCode = String(
    (billingQuery.data as any)?.package ||
      (billingQuery.data as any)?.recommended_package ||
      'package1',
  )
    .trim()
    .toLowerCase();
  const canUseAvatarFeature = resolvedPackageCode === 'package3';
  const isEvaluationsFeatureAvailable = resolvedPackageCode !== 'package1';
  const isAdminInClub = String(currentClubRole || '').toLowerCase() === 'admin';
  const evaluations = React.useMemo(() => {
    if (!isEvaluationsFeatureAvailable) return [];
    const raw = Array.isArray(profile?.coach_feedback) ? profile.coach_feedback : [];
    if (!activeClubId) return mapEvaluationsFromApi(raw);
    const clubId = Number(activeClubId);
    const filtered = raw.filter((item: any) => Number(item?.club_id ?? item?.clubId) === clubId);
    return mapEvaluationsFromApi(filtered);
  }, [activeClubId, profile?.coach_feedback, isEvaluationsFeatureAvailable]);
  const profileAvatar: AvatarSelection = profile?.avatar_selection;

  // Generate all avatar URLs on mount
  useEffect(() => {
    if (!canUseAvatarFeature) {
      setAvatarUrls({});
      setSelectedAvatarUrl('');
      return;
    }
    const generateAllAvatars = async () => {
      setIsGenerating(true);
      const urls: Record<string, string> = {};
      
      for (const avatar of MIXED_AVATARS) {
        try {
          const style = avatar.collection as DiceBearStyle | undefined;
          if (!style) {
            console.warn(`Avatar style missing for ${avatar.id}`);
            continue;
          }
          const dicebear = createAvatar(style, {
            ...DEFAULT_AVATAR_OPTIONS,
            seed: avatar.seed,
          });
          urls[avatar.id] = await dicebear.toDataUri();
        } catch (error) {
          console.error(`Failed to generate avatar ${avatar.id}:`, error);
        }
      }
      
      setAvatarUrls(urls);
      
      // Set initial selected avatar
      if (urls[MIXED_AVATARS[0].id]) {
        setSelectedAvatarUrl(urls[MIXED_AVATARS[0].id]);
      }
      
      setIsGenerating(false);
    };
    
    generateAllAvatars();
  }, [canUseAvatarFeature]);

  useEffect(() => {
    if (!profileAvatar?.avatar_id) return;
    const avatarId = profileAvatar.avatar_id;
    if (avatarUrls[avatarId]) {
      setSelectedAvatarId(avatarId);
      setSelectedAvatarUrl(avatarUrls[avatarId]);
    }
  }, [profileAvatar, avatarUrls]);

  const filteredAvatars = MIXED_AVATARS.filter(avatar => 
    avatar.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    avatar.styleName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    avatar.seed.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentMember = React.useMemo(
    () =>
      profile
        ? {
            id: String(profile.id),
            firstName: profile.first_name,
            lastName: profile.last_name,
            nickName: profile.nickname || '',
            email: profile.email,
            role: String(profile.role || '').toLowerCase(),
            skillLevel: undefined,
            equipment: profile.equipment || [],
            evaluations,
            hasEvaluation: evaluations.length > 0,
          }
        : null,
    [profile, evaluations],
  );
  
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    nickName: '',
    twoFactorEnabled: false,
  });

  useEffect(() => {
    if (currentMember && !isEditing) {
      setFormData((prev) => {
        const next = {
          firstName: currentMember.firstName,
          lastName: currentMember.lastName,
          nickName: currentMember.nickName || '',
          twoFactorEnabled: Boolean(profile?.two_factor_enabled),
        };
        const isSame =
          prev.firstName === next.firstName &&
          prev.lastName === next.lastName &&
          prev.nickName === next.nickName &&
          prev.twoFactorEnabled === next.twoFactorEnabled;
        return isSame ? prev : next;
      });
    }
  }, [currentMember, isEditing, profile?.two_factor_enabled]);

  const memberEvaluations = currentMember?.evaluations || [];
  const acceptedPrivacyPolicy =
    typeof profile?.accept_privacy_policy === 'boolean'
      ? profile.accept_privacy_policy
      : !Boolean(user?.needsConsent);
  const acceptedTermsAndConditions =
    typeof profile?.accept_terms_and_conditions === 'boolean'
      ? profile.accept_terms_and_conditions
      : typeof profile?.accept_data_collection === 'boolean'
      ? profile.accept_data_collection
      : !Boolean(user?.needsConsent);
  const latestEvaluation = memberEvaluations[0];
  const latestRating = latestEvaluation?.rating ?? null;
  const hasEvaluation =
    (currentMember?.role === 'coach') ||
    currentMember?.hasEvaluation ||
    memberEvaluations.length > 0;
  const baseSkill =
    hasEvaluation
      ? latestRating ?? (currentMember?.role === 'coach' ? 5 : undefined)
      : undefined;
  const roundedSkillLevel =
    baseSkill === undefined ? null : Math.min(5, Math.max(0, Math.round(baseSkill * 10) / 10));
  const equipmentCount = currentMember?.equipment?.length || 0;

  const handleInputChange = useCallback((field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSave = useCallback(() => {
    if (!currentMember) return;
    (async () => {
      try {
        await apiClient.request('/profile/update', {
          method: 'PUT',
          body: JSON.stringify({
            first_name: formData.firstName,
            last_name: formData.lastName,
            nickname: formData.nickName,
            two_factor_enabled: formData.twoFactorEnabled,
          }),
        });
        await queryClient.invalidateQueries({ queryKey: ['profile'] });
        toast.success('Profile updated!');
        setIsEditing(false);
      } catch (err: any) {
        toast.error(err?.message || 'Failed to update profile');
      }
    })();
  }, [currentMember, formData, queryClient]);

  const handleSelectAvatar = useCallback(async (avatarId: string, url: string) => {
    if (!canUseAvatarFeature) {
      toast.error('Avatar feature is available on Elite (Package 3) only.');
      return;
    }
    const avatarMeta = MIXED_AVATARS.find(a => a.id === avatarId);
    setSelectedAvatarId(avatarId);
    setSelectedAvatarUrl(url);
    setIsAvatarDialogOpen(false);

    if (!avatarMeta) {
      toast.error('Avatar metadata not found');
      return;
    }

    setIsSavingAvatar(true);
    try {
      await apiClient.request('/profile/avatar', {
        method: 'POST',
        body: JSON.stringify({
          avatar_id: avatarMeta.id,
          avatar_seed: avatarMeta.seed,
          collection_name: normalizeCollectionName(avatarMeta.style),
          avatar_name: avatarMeta.name,
        }),
      });
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
      if (activeClubId) {
        await queryClient.invalidateQueries({ queryKey: ['club-members', Number(activeClubId)] });
        await queryClient.invalidateQueries({ queryKey: ['club-queue', Number(activeClubId)] });
        await queryClient.invalidateQueries({ queryKey: ['match-history', Number(activeClubId)] });
      }
      toast.success('Avatar saved!');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save avatar');
    } finally {
      setIsSavingAvatar(false);
    }
  }, [canUseAvatarFeature, queryClient]);

  if (profileQuery.isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHeaderSkeleton />
        <SectionCardSkeleton lines={6} />
        <CardGridSkeleton count={2} className="grid grid-cols-1 md:grid-cols-2 gap-6" />
      </div>
    );
  }

  if (!currentMember) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <Card className="text-center">
          <CardContent className="pt-12 pb-8">
            <User className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Profile Found</h2>
            <p className="text-muted-foreground">
              Your account is not linked to a member profile yet. Ask an admin to add you as a member.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 animate-fade-in px-3 sm:px-4 lg:px-6 overflow-x-hidden">
      <div>
        <h1 className="text-3xl font-display font-bold text-foreground flex items-center gap-3">
          <User className="text-primary" />
          My Profile
        </h1>
        <p className="text-muted-foreground mt-1">
          View and edit your profile information
        </p>
      </div>


      <div className="flex justify-center">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-xl">
          <Card>
            <CardContent className="p-4 text-center">
              <Target className="w-8 h-8 text-success mx-auto mb-2" />
              {roundedSkillLevel === null ? (
                <>
                  <p className="text-2xl font-bold">Not rated</p>
                  <p className="text-sm text-muted-foreground">Awaiting coach evaluation</p>
                </>
              ) : (
                <>
                  <p className="text-2xl font-bold">{roundedSkillLevel}/5</p>
                  <p className="text-sm text-muted-foreground">Skill Level</p>
                </>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Package className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-2xl font-bold">{equipmentCount}</p>
              <p className="text-sm text-muted-foreground">Equipment</p>
            </CardContent>
          </Card>
        </div>
      </div>


      {/* Avatar Section */}
      <Card>
        <CardHeader>
          <CardTitle>Profile Avatar</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <div
              className={`flex flex-col md:flex-row items-center gap-6 transition-all ${
                !canUseAvatarFeature ? 'blur-[5px] opacity-70 pointer-events-none select-none' : ''
              }`}
            >
            <div className="relative">
              {isGenerating ? (
                <div className="w-32 h-32 rounded-full border-4 border-background shadow-lg flex items-center justify-center bg-muted">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : selectedAvatarUrl ? (
                <img
                  src={selectedAvatarUrl}
                  alt="Profile Avatar"
                  className="w-32 h-32 rounded-full border-4 border-background shadow-lg"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 border-4 border-background shadow-lg flex items-center justify-center">
                  <User size={48} className="text-muted-foreground" />
                </div>
              )}
              <div className="absolute -bottom-2 -right-2">
                <Dialog open={isAvatarDialogOpen} onOpenChange={setIsAvatarDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="icon" className="rounded-full h-10 w-10 shadow-md" disabled={!canUseAvatarFeature}>
                      <Edit2 size={16} />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                      <DialogTitle>Choose Your Avatar</DialogTitle>
                    </DialogHeader>
                    
                    {/* Search Bar */}
                    <div className="px-1 py-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search avatars by name or style..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    {/* Avatar Grid */}
                    <div className="flex-1 overflow-y-auto py-4">
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                        {filteredAvatars.map((avatar) => (
                          <button
                            key={avatar.id}
                            type="button"
                            onClick={() => avatarUrls[avatar.id] && handleSelectAvatar(avatar.id, avatarUrls[avatar.id])}
                            className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all hover:scale-105 hover:border-primary ${
                              selectedAvatarId === avatar.id
                                ? 'border-primary ring-2 ring-primary/20'
                                : 'border-border'
                            }`}
                            title={avatar.name}
                            disabled={!avatarUrls[avatar.id]}
                          >
                            {avatarUrls[avatar.id] ? (
                              <img
                                src={avatarUrls[avatar.id]}
                                alt={avatar.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-muted flex items-center justify-center">
                                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 truncate">
                              {avatar.name}
                            </div>
                          </button>
                        ))}
                      </div>
                      
                      {filteredAvatars.length === 0 && (
                        <div className="text-center py-12">
                          <p className="text-muted-foreground">No avatars found matching "{searchQuery}"</p>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={() => setIsAvatarDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            
            <div className="flex-1 space-y-2">
              <h3 className="font-semibold text-lg">Avatar Selection</h3>
              <p className="text-sm text-muted-foreground">
                Choose an avatar to represent you in the club. This will be visible to coaches and other members.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <div className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                  Selected: {MIXED_AVATARS.find(a => a.id === selectedAvatarId)?.name || 'None'}
                </div>
              </div>
            </div>
            </div>
            {!canUseAvatarFeature && (
              <div className="absolute inset-0 rounded-lg border border-dashed border-red-500 bg-background/10 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="text-center max-w-md">
                  <p className="font-semibold">Avatar feature is available on Elite (Package 3)</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Upgrade to Package 3 to use and manage profile avatars.
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>



      {/* Profile Info */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Personal Information</CardTitle>
          {!isEditing ? (
            <Button variant="outline" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                <Save size={16} className="mr-2" />
                Save
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              {isEditing ? (
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                />
              ) : (
                <p className="py-2 px-3 bg-muted rounded-md">{currentMember.firstName}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              {isEditing ? (
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                />
              ) : (
                <p className="py-2 px-3 bg-muted rounded-md">{currentMember.lastName}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nickName">Nickname</Label>
              {isEditing ? (
                <Input
                  id="nickName"
                  value={formData.nickName}
                  onChange={(e) => handleInputChange('nickName', e.target.value)}
                />
              ) : (
                <p className="py-2 px-3 bg-muted rounded-md">
                  {currentMember.nickName || '\u00a0'}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <p className="py-2 px-3 bg-muted rounded-md">{currentMember.email}</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="block">Security</Label>
            {isEditing ? (
              <label className="flex items-center gap-3 py-2 px-3 bg-muted/50 rounded-md border border-border/60">
                <input
                  type="checkbox"
                  checked={formData.twoFactorEnabled}
                  onChange={(e) => setFormData((prev) => ({ ...prev, twoFactorEnabled: e.target.checked }))}
                  className="h-5 w-5 shrink-0"
                />
                <span className="text-sm text-muted-foreground">
                  Enable 2FA for login (email OTP will be required every time).
                </span>
              </label>
            ) : (
              <p className="py-2 px-3 bg-muted rounded-md">
                {formData.twoFactorEnabled ? 'Enabled (Email OTP required on login)' : 'Disabled'}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Policy Consent</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={acceptedPrivacyPolicy}
              disabled
              readOnly
              className="h-5 w-5 shrink-0"
            />
              <span className="text-sm text-muted-foreground">
                I agree to the{' '}
                <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Privacy Policy
                </Link>
                .
              </span>
          </label>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={acceptedTermsAndConditions}
              disabled
              readOnly
              className="h-5 w-5 shrink-0"
            />
              <span className="text-sm text-muted-foreground">
                I agree to the{' '}
                <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Terms and Conditions
                </Link>
                .
              </span>
          </label>
        </CardContent>
      </Card>
      

      {/* Evaluations - hide for coaches */}
      {user?.role !== 'coach' && (
        <div className="w-full">
          <Card className="w-full">
            <CardHeader>
              <CardTitle>Evaluations</CardTitle>
            </CardHeader>
            <CardContent>
              {currentMember.evaluations.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Coach</TableHead>
                        <TableHead className="text-center">Average</TableHead>
                        <TableHead className="text-center">Technical</TableHead>
                        <TableHead className="text-center">Movement</TableHead>
                        <TableHead className="text-center">Tactical</TableHead>
                        <TableHead className="text-center">Physical</TableHead>
                        <TableHead className="text-center">Psychological</TableHead>
                        <TableHead>Quarter Plan</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentMember.evaluations.map((evaluation) => {
                        const categories = evaluation.categories || {};
                        return (
                          <TableRow key={evaluation.id}>
                            <TableCell className="whitespace-nowrap">
                              {evaluation.date ? new Date(evaluation.date).toLocaleString() : ''}
                            </TableCell>
                            <TableCell>{evaluation.coachName || 'Admin'}</TableCell>
                            <TableCell className="text-center">
                              <StarRating value={evaluation.rating} size={14} />
                            </TableCell>
                            <TableCell className="text-center">
                              <StarRating value={categories.technical} size={14} />
                            </TableCell>
                            <TableCell className="text-center">
                              <StarRating value={categories.movement} size={14} />
                            </TableCell>
                            <TableCell className="text-center">
                              <StarRating value={categories.tactical} size={14} />
                            </TableCell>
                            <TableCell className="text-center">
                              <StarRating value={categories.physical} size={14} />
                            </TableCell>
                            <TableCell className="text-center">
                              <StarRating value={categories.psychological} size={14} />
                            </TableCell>
                            <TableCell className="min-w-[220px]">
                              {evaluation.quarterPlan || evaluation.feedback || ''}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                isEvaluationsFeatureAvailable ? (
                  <p className="text-muted-foreground text-center py-6">
                    No evaluations yet
                  </p>
                ) : (
                  <div className="rounded-lg border border-dashed border-red-500 bg-background/20 p-5 text-center">
                    <p className="font-semibold">{t('profile_evaluations_unavailable_starter', 'Evaluations are unavailable on Starter (Package 1)')}</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t('profile_upgrade_pro_elite_evaluations', 'Upgrade to Pro or Elite to view evaluation history and category scores.')}
                    </p>
                    {isAdminInClub ? (
                      <p className="mt-3 text-xs text-muted-foreground">
                        {t('profile_change_package_from_club_settings', 'You can change the package from Club settings.')}
                      </p>
                    ) : (
                      <p className="mt-3 text-xs text-muted-foreground">
                        Please contact your club admin to unlock evaluations.
                      </p>
                    )}
                  </div>
                )
              )}
            </CardContent>
          </Card>

        </div>
      )}
    </div>
  );
}
